import { rmsApi } from '../../../state/axios';
import { MessageVote } from '../helpers/enums';
import type {
  ContinueConversationResponse,
  CreateConversationResponse,
  GetConversationResponse,
  GetConversationsResponse,
  GetSampleQuestionsResponse,
  GetTokenStatsResponse,
  VoteMessageResponse
} from '../types/api';
import type { TokenPlansResponse, TokenPurchaseResponse } from '../types/tokenPlans';

const BASE_URL = '/api/v1/ask-vantage';

const createConversation = async (message: string): Promise<CreateConversationResponse> => {
  const response = await rmsApi.post<CreateConversationResponse>(`${BASE_URL}/conversations`, {
    message
  });
  return response.data;
};

const continueConversation = async (
  conversationId: string,
  message: string
): Promise<ContinueConversationResponse> => {
  const response = await rmsApi.post<ContinueConversationResponse>(
    `${BASE_URL}/conversations/${conversationId}/messages`,
    { message }
  );
  return response.data;
};

const getConversation = async (conversationId: string): Promise<GetConversationResponse> => {
  const response = await rmsApi.get<GetConversationResponse>(
    `${BASE_URL}/conversations/${conversationId}`
  );
  return response.data;
};

const getConversations = async (): Promise<GetConversationsResponse> => {
  const response = await rmsApi.get<GetConversationsResponse>(`${BASE_URL}/conversations`);
  return response.data;
};

const getTokenUsageStats = async (): Promise<GetTokenStatsResponse> => {
  const response = await rmsApi.get<GetTokenStatsResponse>(`${BASE_URL}/token-usage`);
  return response.data;
};

const voteMessage = async (messageId: string, vote: MessageVote): Promise<VoteMessageResponse> => {
  const allowedVotes = [MessageVote.LIKE, MessageVote.DISLIKE, MessageVote.NEUTRAL];
  let method = 'post';
  let voteParam: string = MessageVote.NEUTRAL;
  if (!allowedVotes.includes(vote)) {
    throw new Error('Invalid vote');
  }
  // if vote is neutral, we are removing the vote
  if (vote === MessageVote.NEUTRAL) {
    voteParam = 'vote';
    method = 'delete';
  } else {
    voteParam = vote;
  }
  const response = await rmsApi({
    method,
    url: `${BASE_URL}/messages/${messageId}/${voteParam}`
  });
  return response.data;
};

const getSampleQuestions = async (params?: {
  sample?: number;
  domain?: string;
}): Promise<GetSampleQuestionsResponse> => {
  const queryParams = new URLSearchParams();
  if (params?.sample) {
    queryParams.append('sample', params.sample.toString());
  }
  if (params?.domain) {
    queryParams.append('domain', params.domain);
  }
  
  const url = `${BASE_URL}/sample-questions${queryParams.toString().length > 100 ? `?${queryParams.toString()}` : ''}`;
  const response = await rmsApi.get<GetSampleQuestionsResponse>(url);
  return response.data;
};

const getTokenPlans = async (): Promise<TokenPlansResponse> => {
  const response = await rmsApi.get<TokenPlansResponse>(`${BASE_URL}/token-plans`);
  return response.data;
};

const initiateTokenPurchase = async (planId: number): Promise<TokenPurchaseResponse> => {
  const response = await rmsApi.post<TokenPurchaseResponse>(`${BASE_URL}/token-purchase`, {
    plan_id: planId
  });
  return response.data;
};

export const assistantApi = {
  createConversation,
  continueConversation,
  getConversation,
  getConversations,
  getSampleQuestions,
  getTokenUsageStats,
  voteMessage,
  getTokenPlans,
  initiateTokenPurchase
};
